import React, {
  FocusEventHandler,
  KeyboardEventHandler,
  useEffect,
  useState,
} from 'react'

import InfoIcon from '@mui/icons-material/Info'
import { Autocomplete, Box, Chip, TextField } from '@mui/material'
import IconButton from '@mui/material/IconButton'

import ConditionalTooltip from './ConditionalTooltip'
import { isScreenSizeSmall } from './utils'

type MultiInputTextFieldProps = {
  name: string
  label: string
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  onChange?: (event: React.SyntheticEvent<Element, Event>) => void
  placeholder?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  value: string
  error: string
  disabled?: boolean
  inputs: string[]
  selectedInput: (items: string[]) => void
  tooltip?: string
  validateFn?: (items: string[]) => boolean
  required?: boolean
  style?: React.CSSProperties
}

const MultiInputTextField = ({
  name,
  label,
  placeholder,
  value,
  error,
  inputs,
  selectedInput,
  tooltip,
  validateFn,
  required,
  style,
}: MultiInputTextFieldProps) => {
  let wrapperClass = 'form-group'
  if (error && error.length > 0) {
    wrapperClass += ' ' + 'has-error'
  }

  const [inputValue, setInputValue] = useState('')
  const [selectedItem, setSelectedItem] = useState<Array<string> | null>(null)
  useEffect(() => {
    if (value !== undefined && value !== null && value !== '') {
      setSelectedItem(inputs)
    } else {
      setSelectedItem(null)
    }
  }, [inputs, value])

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      const newSelectedItem = [...(selectedItem || [])] as string[]
      const selectedItems = [...(selectedItem || [])] as string[]
      const duplicatedValues = newSelectedItem.indexOf(
        event.target.value.trim(),
      )
      if (duplicatedValues !== -1) {
        setSelectedItem(selectedItems)
        setInputValue('')
        return
      }
      if (!event.target.value.replace(/\s/g, '').length) return

      newSelectedItem.push(event.target.value.trim())
      const isValid = validateFn ? validateFn(newSelectedItem) : true
      if (isValid) {
        setSelectedItem(newSelectedItem)
        setInputValue('')
        selectedInput(newSelectedItem)
      } else {
        setSelectedItem(selectedItems)
        setInputValue('')
        return
      }
    }
    if (
      !!selectedItem &&
      selectedItem.length &&
      !inputValue.length &&
      event.key === 'Backspace'
    ) {
      if (event.target.value) return
      const newSelectedItem = selectedItem.slice(0, selectedItem.length - 1)
      setSelectedItem(newSelectedItem)
      selectedInput(newSelectedItem)
      event.preventDefault()
    }
  }

  const handleDelete = (item: string) => () => {
    const newSelectedItem = [...(selectedItem || [])] as string[]
    newSelectedItem.splice(newSelectedItem.indexOf(item), 1)
    const newValue = [...newSelectedItem]
    setSelectedItem(newValue)
    selectedInput(newValue)
  }

  return (
    <React.Fragment>
      <label htmlFor={name}>
        {label}
        {required && (
          <Box component="span" color="error.main" aria-hidden="true">
            *
          </Box>
        )}
      </label>
      <span>
        <ConditionalTooltip title={tooltip ?? ''}>
          <IconButton>
            <InfoIcon fontSize={isScreenSizeSmall() ? 'large' : 'small'} />
          </IconButton>
        </ConditionalTooltip>
      </span>
      <Autocomplete
        multiple={true}
        clearIcon={false}
        options={[]}
        freeSolo
        style={style}
        onKeyDown={handleKeyDown}
        value={selectedItem || []}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue)
        }}
        renderTags={(value) =>
          value.map((item) => (
            <Chip
              key={item}
              tabIndex={-1}
              label={item}
              onDelete={handleDelete(item)}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            placeholder={placeholder}
            {...params}
            InputProps={{
              style: isScreenSizeSmall() ? { fontSize: '2.5rem' } : {},
            }}
          />
        )}
      />
      {error && (
        <div className="responsive-font alert alert-danger">{error}</div>
      )}
    </React.Fragment>
  )
}
export default MultiInputTextField
